import { createFileRoute, Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import { LegalFooter } from "@/components/marketing/legal-page";

export const Route = createFileRoute("/compare")({
  head: () => ({
    meta: [
      { title: "CapitalDart vs. Alternatives | The Capital Raising Platform" },
      {
        name: "description",
        content:
          "The only end-to-end capital raising platform, AI matching you to the right person at each investor, rich contact/company/investment data, bespoke outreach, warm intros, strategy, diligence, and deal tracking.",
      },
      {
        property: "og:title",
        content: "CapitalDart vs. Alternatives | The Capital Raising Platform",
      },
      {
        property: "og:description",
        content:
          "The only end-to-end capital raising platform, AI matching you to the right person at each investor, rich contact/company/investment data, bespoke outreach, warm intros, strategy, diligence, and deal tracking.",
      },
      {
        name: "twitter:description",
        content:
          "CapitalDart: find the right investors, reach the right contacts, and run a better capital raise. Curated 70,000+ institutional contact database with outreach written in your voice.",
      },
      { property: "og:url", content: "https://capitaldart.com/compare" },
      { property: "og:image", content: "https://capitaldart.com/capitaldart-og.png" },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/compare" }],
  }),
  component: ComparePage,
});

type Col = { name: string; sub: string; highlight?: boolean };

const COLUMNS: Col[] = [
  { name: "Placement Agents", sub: "Park Hill, Eaton Partners, Campbell Lutyens" },
  { name: "Databases", sub: "PitchBook, Preqin, Crunchbase" },
  { name: "AI Tools", sub: "PipelineRoad, Evalyze, PitchVault, Qubit Capital" },
  { name: "CapitalDart", sub: "Institutional capital, end-to-end", highlight: true },
];

const ROWS: Array<{ label: string; values: [string, string, string, string] }> = [
  {
    label: "What it is",
    values: [
      "Sell-side advisory firms running fundraises for institutional issuers",
      "Subscription investor research databases",
      "AI-powered investor matching and outreach platforms",
      "AI-powered institutional capital raise platform with a curated 70,000+ contact database, six AI reports, and Claude's Rating on every contact. Works inside ChatGPT and Claude.",
    ],
  },
  {
    label: "Pricing",
    values: [
      "2–3% placement fee + $15–25K/mo retainer + 0.5–1% tail fees",
      "$15K–$50K per year per seat",
      "Free to $999/mo or per-analysis fees",
      "$500 One Plan. 500 credits, 5 reports included, unlimited Deal Update Reports. $100 per 100-credit top-up. No retainer. No placement fee. No tail.",
    ],
  },
  {
    label: "Time to outreach-ready",
    values: ["3–6 months", "You write everything yourself", "1–4 weeks", "10 to 30 minutes"],
  },
  {
    label: "Investor database",
    values: [
      "Personal networks built over decades",
      "100K+ firm-level entries",
      "10K–20K names, VC-skewed",
      "70,000+ institutional contacts, contact-level detail",
    ],
  },
  {
    label: "Asset class coverage",
    values: [
      "PE funds, VC funds, fund-of-funds",
      "Varies (PitchBook: PE/VC, Preqin: alts, Crunchbase: startups)",
      "SaaS, seed-stage tech",
      "PE, VC, real estate, structured credit, specialty finance, family offices, pensions, insurance, sovereign wealth, infrastructure",
    ],
  },
  {
    label: "Outreach written for you",
    values: [
      "Yes, by senior bankers",
      "No",
      "Yes, founder-friendly tone",
      "Yes, institutional banker voice. No em dashes. No AI-tells.",
    ],
  },
  {
    label: "Investor Readiness Report",
    values: [
      "Yes, by team",
      "No",
      "Partial (basic deck scoring)",
      "Yes. Full 15-section IRR, slide-by-slide review",
    ],
  },
  {
    label: "Financial Model Review",
    values: [
      "Yes, by team",
      "No",
      "Partial (spreadsheet checks)",
      "Yes. Model audit, sanity checks, and executable stress tests",
    ],
  },
  {
    label: "Raise Strategy Report",
    values: [
      "Yes, by team",
      "No",
      "No",
      "Yes. Pacing, channels, positioning by investor type, send order, and a weekly working plan",
    ],
  },
  {
    label: "Deal Update Report",
    values: [
      "Yes, by team",
      "No",
      "No",
      "Yes. Status movement, engagement by type, priorities, stall points, and an expansion call",
    ],
  },
  {
    label: "Market Comps and Valuation Report",
    values: [
      "Yes, by team",
      "No",
      "Partial (public comps only)",
      "Yes. Comparable transactions, valuation ranges, and buyer rationale",
    ],
  },
  {
    label: "Indicative Term Sheet",
    values: [
      "Yes, by team",
      "No",
      "No",
      "Yes. Non-binding terms for equity, debt, or sale mandates",
    ],
  },
  {
    label: "Claude's Rating",
    values: [
      "Manual",
      "No",
      "Basic fit scores",
      "Yes. 1 to 100 fit score with rationale on every investor and contact",
    ],
  },
  {
    label: "Platform acquirers",
    values: [
      "Yes, by team",
      "No",
      "No",
      "Yes. Surfaced for sale mandates, including sponsor-backed platforms rolling up the space",
    ],
  },
  {
    label: "Works inside ChatGPT and Claude",
    values: ["No", "No", "No", "Yes. Published connector in both directories"],
  },
  {
    label: "Pipeline tracking",
    values: ["Quarterly reports", "No", "Varies by tool", "Built-in status funnel"],
  },
  {
    label: "Best for",
    values: [
      "Funds raising $200M+ with bandwidth for a 12–18 month engagement",
      "Internal investor research teams with bandwidth to write all outreach",
      "Seed-stage tech founders raising from VCs",
      "Lower middle market companies, fund GPs raising Fund I through III, real estate sponsors, and alternative asset issuers raising $5M and up.",
    ],
  },
];

function ComparePage() {
  return (
    <main className="min-h-screen bg-brand-paper-2 px-4 py-12 sm:py-16">
      <article className="relative mx-auto max-w-[1080px] overflow-hidden rounded-2xl border border-brand-line bg-brand-paper shadow-[0_1px_0_var(--brand-line),0_30px_60px_-30px_oklch(0.264_0.068_255.3/0.18)]">
        <nav className="flex items-center justify-between border-b border-brand-line px-[22px] py-4 lg:px-10">
          <Link to="/" className="flex items-center gap-2 text-brand-ink no-underline">
            <Logo
              src="/capitaldart-logo.png"
              alt="CapitalDart"
              className="h-9 w-auto"
              height={36}
              trademarkClassName="text-brand-ink"
            />
          </Link>
          <a
            href="https://capitaldart.com"
            className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
          >
            ← Back to capitaldart.com
          </a>
        </nav>

        <div className="px-[22px] pt-9 pb-10 lg:px-10 lg:pt-12 lg:pb-14">
          <div className="font-mono text-[14px] uppercase tracking-[0.18em] text-brand-accent">
            Compare
          </div>
          <h1 className="mt-3 font-serif text-[34px] leading-[1.1] text-brand-ink lg:text-[44px]">
            How CapitalDart compares
          </h1>
          <p className="mt-4 max-w-[760px] text-[16px] leading-[1.55] text-brand-ink-2 lg:text-[18px]">
            Where placement agents, databases, and AI tools fit, and where they do not. A complete
            side-by-side.
          </p>

          <div className="mt-7 max-w-[820px] space-y-4 text-[14.5px] leading-[1.65] text-brand-ink lg:text-[15.5px]">
            <p>
              Companies raising capital have three real choices today, plus a growing set of
              AI-powered tools. Placement agents and investment banks are the traditional path: deep
              relationships, full service, expensive. Subscription databases like PitchBook give you
              names and let you do the work. New AI tools promise faster matching, mostly for
              seed-stage SaaS founders.
            </p>
            <p>
              CapitalDart is built for everything in between and beyond: lower middle market
              companies, fund GPs, real estate sponsors, structured credit issuers, and any
              institutional raise that needs both a curated database and outreach written in an
              institutional voice.
            </p>
            <p>
              CapitalDart now includes six AI reports, Claude's Rating on every investor and
              contact, platform acquirer surfacing for sale mandates, and published connectors that
              work inside ChatGPT and Claude.
            </p>
          </div>

          {/* Desktop matrix */}
          <div className="mt-10 hidden lg:block">
            <div className="overflow-hidden rounded-lg border border-brand-line">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-brand-line bg-brand-paper-2">
                    <th className="w-[180px] px-4 py-5 align-bottom" />
                    {COLUMNS.map((c) => (
                      <th
                        key={c.name}
                        className={
                          "px-4 py-5 align-bottom " +
                          (c.highlight ? "border-l-2 border-brand-accent bg-brand-paper" : "")
                        }
                      >
                        <div
                          className={
                            "font-serif text-[18px] leading-[1.2] " +
                            (c.highlight ? "text-brand-ink" : "text-brand-ink")
                          }
                        >
                          {c.name}
                        </div>
                        <div
                          className={
                            "mt-1.5 font-mono text-[14px] uppercase tracking-[0.16em] text-brand-muted " +
                            (c.highlight ? "border-b border-brand-accent pb-2" : "")
                          }
                        >
                          {c.sub}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.label} className="border-b border-brand-line last:border-b-0">
                      <td className="w-[180px] px-4 py-5 align-top font-mono text-[14px] uppercase tracking-[0.18em] text-brand-accent">
                        {row.label}
                      </td>
                      {row.values.map((v, i) => {
                        const highlight = COLUMNS[i]!.highlight;
                        return (
                          <td
                            key={i}
                            className={
                              "px-4 py-5 align-top text-[13.5px] leading-[1.55] " +
                              (highlight
                                ? "border-l-2 border-brand-accent bg-brand-paper font-medium text-brand-ink"
                                : "text-brand-ink-2")
                            }
                          >
                            {v}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile stacked cards: CapitalDart first */}
          <div className="mt-10 space-y-5 lg:hidden">
            {[3, 0, 1, 2].map((idx) => {
              const col = COLUMNS[idx]!;
              return (
                <div
                  key={col.name}
                  className={
                    "overflow-hidden rounded-lg border " +
                    (col.highlight
                      ? "border-brand-line border-l-2 border-l-brand-accent bg-brand-paper-2"
                      : "border-brand-line bg-brand-paper")
                  }
                >
                  <div className="border-b border-brand-line px-4 py-4">
                    <div className="font-serif text-[20px] leading-[1.2] text-brand-ink">
                      {col.name}
                    </div>
                    <div className="mt-1 font-mono text-[14px] uppercase tracking-[0.16em] text-brand-muted">
                      {col.sub}
                    </div>
                  </div>
                  <dl className="divide-y divide-brand-line">
                    {ROWS.map((row) => (
                      <div key={row.label} className="px-4 py-4">
                        <dt className="font-mono text-[14px] uppercase tracking-[0.18em] text-brand-accent">
                          {row.label}
                        </dt>
                        <dd
                          className={
                            "mt-1.5 text-[14px] leading-[1.55] " +
                            (col.highlight ? "font-medium text-brand-ink" : "text-brand-ink-2")
                          }
                        >
                          {row.values[idx]}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })}
          </div>

          {/* CTA block */}
          <div className="mt-14 lg:mt-20">
            <h2 className="font-serif text-[28px] leading-[1.15] text-brand-ink lg:text-[36px]">
              Try it. Your first sample is free.
            </h2>
            <p className="mt-3 max-w-[680px] text-[14.5px] leading-[1.55] text-brand-ink-2 lg:text-[15px]">
              Upload your deck, see the matched investor list, the Investor Readiness Report, and
              the outreach drafts. No credit card. No commitment.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/auth"
                search={{ mode: "signup" as const }}
                className="inline-flex items-center justify-center rounded-md bg-brand-ink px-5 py-3 text-[14px] font-medium text-white no-underline transition-opacity hover:opacity-90"
              >
                Start a free sample →
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center justify-center rounded-md border border-brand-line bg-transparent px-5 py-3 text-[14px] font-medium text-brand-ink no-underline transition-colors hover:bg-brand-paper-2"
              >
                See sample IRR
              </Link>
            </div>
          </div>

          {/* Divider */}
          <div
            className="mt-14 flex items-center justify-center text-brand-line lg:mt-20"
            aria-hidden
          >
            <span className="font-serif text-[18px] text-brand-accent">✦</span>
          </div>

          {/* Cross-link */}
          <div className="mt-10 text-center">
            <h3 className="font-serif text-[22px] text-brand-ink lg:text-[26px]">
              More questions?
            </h3>
            <p className="mt-3 text-[14.5px] leading-[1.55] text-brand-ink-2">
              <a href="/#faq" className="text-brand-ink no-underline hover:text-brand-accent">
                See the full FAQ <span className="text-brand-accent">→</span>
              </a>
            </p>
          </div>
        </div>
        <LegalFooter />
      </article>
    </main>
  );
}
